import { useEffect, useRef, useState } from 'react';
import { Button, Dropdown, Header, Icon, Input, Label, Message, Modal, Pagination, Table } from 'react-frontier';
import { useBreadcrumbs } from '../Hooks';
import { bindClick, formatUnix, inputToUnix, TIMEZONE } from '../SiteUtil';
import { DateTime } from 'luxon';
import { ExternalEvent, ExternalUploadResult, SetLoading } from '../SiteClasses';
import UploadBox, { UploadBoxRef } from '../components/UploadBox';
import Toast from '../Toast';
import API from '../API';
import * as XLSX from 'xlsx';

const PAGE_SIZE = 30;
const MAX_ROWS = 5000;
const REQUIRED_HEADERS = ['Evento', 'Recinto', 'Fecha'];

var headerKey = (v: any)=>String(v ?? '').replace(/\s+/g, ' ').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

function cellValue(v: any){
	if(v instanceof Date) return `${v.getDate()}/${v.getMonth() + 1}/${v.getFullYear()}`;
	return v ?? null;
}

function sheetToRows(sheet: XLSX.WorkSheet){
	var grid = XLSX.utils.sheet_to_json<any[]>(sheet, { header: 1, defval: null, raw: true, blankrows: true });
	var required = REQUIRED_HEADERS.map(headerKey);
	var headerIndex = grid.findIndex(r=>{
		var found = (r || []).map(headerKey);
		return required.every(c=>found.includes(c));
	});
	if(headerIndex<0) return null;

	var headers = (grid[headerIndex] || []).map(h=>String(h ?? '').replace(/\s+/g, ' ').trim());
	var rows = grid.slice(headerIndex + 1).map(r=>{
		var o: { [column: string]: any } = {};
		headers.forEach((h, i)=>{ if(h) o[h] = cellValue((r || [])[i]); });
		return o;
	});
	while(rows.length > 0 && Object.values(rows[rows.length - 1]).every(v=>v===null || v==='')) rows.pop();

	return { rows, first_row: headerIndex + 2 };
}

var ExternalEvents = ()=>{
	var { setTitle } = useBreadcrumbs();
	var uploadRef = useRef<UploadBoxRef>(null);

	var [events, setEvents] = useState<ExternalEvent[]>(null);
	var [sheet, setSheet] = useState<{ rows: { [column: string]: any }[], first_row: number }>(null);
	var [fileName, setFileName] = useState<string>(null);
	var [result, setResult] = useState<ExternalUploadResult>(null);
	var [prompts, setPrompts] = useState<string[]>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [uploadOpen, setUploadOpen] = useState<boolean>(false);
	var [page, setPage] = useState<number>(0);
	var [filter, setFilter] = useState<string>('');
	var [sellerFilter, setSellerFilter] = useState<string>('');
	var [typeFilter, setTypeFilter] = useState<string>('');
	var [companyFilter, setCompanyFilter] = useState<string>('');
	var [exclusiveFilter, setExclusiveFilter] = useState<string>('');
	var [dateOpen, setDateOpen] = useState<boolean>(false);
	var [dateStart, setDateStart] = useState<string>('');
	var [dateEnd, setDateEnd] = useState<string>('');
	var [modalStart, setModalStart] = useState<string>('');
	var [modalEnd, setModalEnd] = useState<string>('');
	var [datePrompts, setDatePrompts] = useState<string[]>(null);

	var load = ()=>{
		setLoadError(null);
		API.getExternalEvents().then(res=>{
			if(res.error) return setLoadError(res.message);
			setEvents(res.data || []);
			setPage(0);
		}).catch(()=>setLoadError('Hubo un error cargando los eventos externos. (LCL-1)'));
	}

	useEffect(()=>{
		setTitle('Eventos externos');
		load();
	}, []);

	var clearFile = ()=>{
		uploadRef.current?.clear();
		setSheet(null);
		setFileName(null);
	}

	var openUpload = ()=>{
		clearFile();
		setResult(null);
		setPrompts(null);
		setUploadOpen(true);
	}

	var onFile = (file: File | null)=>{
		setPrompts(null);
		setResult(null);
		if(!file) return setSheet(null);

		file.arrayBuffer().then(buf=>{
			var wb = XLSX.read(buf, { type: 'array', cellDates: true });
			var ws = wb.Sheets[wb.SheetNames[0]];
			if(!ws){
				clearFile();
				return Toast.error('El archivo no tiene hojas.');
			}
			var parsed = sheetToRows(ws);
			if(!parsed){
				clearFile();
				return Toast.error(`No se encontró la fila de encabezados. Debe tener las columnas ${REQUIRED_HEADERS.join(', ')}.`);
			}
			if(parsed.rows.length===0){
				clearFile();
				return Toast.error('El archivo no tiene filas debajo de los encabezados.');
			}
			if(parsed.rows.length > MAX_ROWS){
				clearFile();
				return Toast.error(`El archivo trae ${parsed.rows.length} filas, el máximo es ${MAX_ROWS}.`);
			}
			setSheet(parsed);
			setFileName(file.name);
		}).catch(()=>{
			clearFile();
			Toast.error('No se pudo leer el archivo, revisa que sea un Excel válido.');
		});
	}

	var submit = (setLoading: SetLoading)=>{
		if(!sheet) return setPrompts(['Selecciona el archivo de marketing.']);

		setLoading(true);
		API.uploadExternalEvents(sheet.rows, sheet.first_row).then(res=>{
			if(res.error) return setPrompts([res.message]);
			clearFile();
			setResult(res.data);
			if(res.data.inserted + res.data.updated > 0) Toast.success('Archivo cargado');
			load();
		}).catch(()=>setPrompts(['Hubo un error cargando el archivo. (LCL-2)']))
		.finally(()=>setLoading(false));
	}

	var options = (values: string[])=>[{ text: 'Todos', value: '' }, ...Array.from(new Set(values.filter(v=>!!v))).sort().map(v=>({ text: v, value: v }))];
	var seller_options = options((events || []).map(e=>e.seller_name));
	var type_options = options((events || []).map(e=>e.category_name));
	var company_options = options((events || []).map(e=>e.company_name));

	var openDate = ()=>{
		var now = DateTime.now().setZone(TIMEZONE);
		setModalStart(dateStart || now.startOf('month').toFormat('yyyy-MM-dd'));
		setModalEnd(dateEnd || now.endOf('month').toFormat('yyyy-MM-dd'));
		setDatePrompts(null);
		setDateOpen(true);
	}

	var applyDate = ()=>{
		var start = modalStart ? inputToUnix(modalStart) : null;
		var end = modalEnd ? inputToUnix(modalEnd) : null;
		if((modalStart && start===null) || (modalEnd && end===null)) return setDatePrompts(['Selecciona fechas válidas.']);
		if(start!==null && end!==null && start > end) return setDatePrompts(['La fecha de inicio no puede ser mayor a la fecha de fin.']);
		setDateStart(modalStart);
		setDateEnd(modalEnd);
		setPage(0);
		setDateOpen(false);
	}

	var clearDate = ()=>{
		setDateStart('');
		setDateEnd('');
		setPage(0);
		setDateOpen(false);
	}

	var start_unix = dateStart ? inputToUnix(dateStart) : null;
	var end_unix = dateEnd ? inputToUnix(dateEnd) : null;

	var search = filter.trim().toLowerCase();
	var filtered = (events || []).filter(e=>(
		(!search || [e.event_name, e.city, e.state, e.venue_name].some(v=>v && v.toLowerCase().includes(search)))
		&& (!sellerFilter || e.seller_name===sellerFilter)
		&& (!typeFilter || e.category_name===typeFilter)
		&& (!companyFilter || e.company_name===companyFilter)
		&& (!exclusiveFilter || String(e.exclusive ? 1 : 0)===exclusiveFilter)
		&& (start_unix===null || (!!e.date && e.date >= start_unix))
		&& (end_unix===null || (!!e.date && e.date <= end_unix + 86399))
	));
	var shown_events = filtered.slice(page * PAGE_SIZE, (page * PAGE_SIZE) + PAGE_SIZE);

	return <div style={{ paddingTop: 20 }}>
		{loadError ? <Header text='Error' subtext={loadError} style={{ marginTop: 30 }} /> : (<>
			<Table fitted striped style={{ maxWidth: 1100, margin: '0 auto' }}>
				<Table.Head
					title='Eventos externos'
					titleRight={<div style={{ display: 'flex', gap: 8 }}>
						<Button
							iconName='calendar'
							text={dateStart || dateEnd ? `${start_unix!==null ? formatUnix(start_unix, 'dd/MMM/yy') : '...'} - ${end_unix!==null ? formatUnix(end_unix, 'dd/MMM/yy') : '...'}` : 'Fecha'}
							color='black'
							size='small'
							onClick={openDate}
							style={{ margin: 0 }}
						/>
						<Button iconName='upload' color='black' text='Cargar' size='small' onClick={openUpload} style={{ margin: 0 }} />
					</div>}
				>
					<Table.Row>
						<Table.Cell colSpan={11} style={{ padding: 8 }}>
							<div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
								<Input fluid placeholder='Buscar evento, ciudad, estado o recinto...' value={filter} onChange={v=>{ setFilter(v); setPage(0); }} iconName='search' style={{ margin: 0, flex: 1 }} />
								<Dropdown
									search
									label='Vendedor'
									placeholder='Vendedor'
									value={sellerFilter}
									items={seller_options}
									onValueChange={v=>{ setSellerFilter(v || ''); setPage(0); }}
									style={{ minWidth: 180, margin: 0 }}
								/>
								<Dropdown
									search
									label='Tipo'
									placeholder='Tipo'
									value={typeFilter}
									items={type_options}
									onValueChange={v=>{ setTypeFilter(v || ''); setPage(0); }}
									style={{ minWidth: 160, margin: 0 }}
								/>
								<Dropdown
									search
									label='Boletera'
									placeholder='Boletera'
									value={companyFilter}
									items={company_options}
									onValueChange={v=>{ setCompanyFilter(v || ''); setPage(0); }}
									style={{ minWidth: 180, margin: 0 }}
								/>
								<Dropdown
									label='Exclusivo'
									placeholder='Exclusivo'
									value={exclusiveFilter}
									items={[{ text: 'Todos', value: '' }, { text: 'Sí', value: '1' }, { text: 'No', value: '0' }]}
									onValueChange={v=>{ setExclusiveFilter(v || ''); setPage(0); }}
									style={{ minWidth: 140, margin: 0 }}
								/>
							</div>
						</Table.Cell>
					</Table.Row>
					<Table.Row>
						<Table.Cell header collapsing>ID</Table.Cell>
						<Table.Cell header>Evento</Table.Cell>
						<Table.Cell header collapsing>Tipo</Table.Cell>
						<Table.Cell header>Recinto</Table.Cell>
						<Table.Cell header>Ciudad</Table.Cell>
						<Table.Cell header collapsing>Aforo</Table.Cell>
						<Table.Cell header>Boletera</Table.Cell>
						<Table.Cell header>Promotor</Table.Cell>
						<Table.Cell header>Vendedor</Table.Cell>
						<Table.Cell header collapsing centered>Exclusivo</Table.Cell>
						<Table.Cell header collapsing>Fecha</Table.Cell>
					</Table.Row>
				</Table.Head>
				<Table.Body>
					{!events ? (
						<Table.Row><Table.Cell colSpan={11} centered><Header loading text='Cargando datos' /></Table.Cell></Table.Row>
					) : shown_events.length===0 ? (
						<Table.Row empty><Table.Cell colSpan={11} centered>{filtered.length===0 && (events || []).length > 0 ? 'No hay eventos que coincidan con los filtros.' : 'No hay eventos externos cargados.'}</Table.Cell></Table.Row>
					) : shown_events.map(e=>(
						<Table.Row key={e.external_id}>
							<Table.Cell collapsing>{e.external_id}</Table.Cell>
							<Table.Cell>{e.event_name}</Table.Cell>
							<Table.Cell collapsing centered>{e.category_name || <Icon name='minus' />}</Table.Cell>
							<Table.Cell>{e.venue_name}</Table.Cell>
							<Table.Cell>{e.city || '-'}</Table.Cell>
							<Table.Cell collapsing centered>{e.capacity ? e.capacity.toLocaleString('es-MX') : <Icon name='minus' />}</Table.Cell>
							<Table.Cell>{e.company_name || '-'}</Table.Cell>
							<Table.Cell>{e.promoter_name || <Icon name='minus' />}</Table.Cell>
							<Table.Cell>{e.seller_name || <Icon name='minus' />}</Table.Cell>
							<Table.Cell collapsing centered><Label color={e.exclusive ? 'green' : 'transparent'}>{e.exclusive ? 'Sí' : 'No'}</Label></Table.Cell>
							<Table.Cell collapsing>{formatUnix(e.date, 'dd/MMM/yyyy')}</Table.Cell>
						</Table.Row>
					))}
				</Table.Body>
			</Table>
			{(filtered.length > PAGE_SIZE || page !== 0) && (
				<div style={{ maxWidth: 1100, margin: '10px auto 0' }}>
					<Pagination
						page={page}
						pageCount={Math.ceil(filtered.length / PAGE_SIZE)}
						onPageChange={setPage}
					/>
				</div>
			)}
		</>)}

		<Modal open={dateOpen} onClose={bindClick(setDateOpen, false)} size='tiny'>
			<Modal.Header text='Filtrar por fecha' />
			<Modal.Content>
				<div style={{ display: 'flex', gap: 12 }}>
					<Input label='Fecha inicio' inputType='date' value={modalStart} onChange={setModalStart} style={{ margin: 0, flex: 1 }} />
					<Input label='Fecha fin' inputType='date' value={modalEnd} onChange={setModalEnd} style={{ margin: 0, flex: 1 }} />
				</div>
				<Message type='error' list={datePrompts} style={{ marginTop: 12 }} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Limpiar' basic size='small' onClick={clearDate} />
				<Button text='Aplicar' color='black' size='small' onClick={applyDate} />
			</Modal.Actions>
		</Modal>

		<Modal open={uploadOpen} onClose={bindClick(setUploadOpen, false)} size='small'>
			<Modal.Header text='Cargar eventos externos' />
			<Modal.Content>
				<UploadBox
					ref={uploadRef}
					icon={sheet ? 'file-excel' : 'upload'}
					accept='.xlsx,.xls'
					maxFileSize={10 * 1024 * 1024}
					text={sheet ? `${fileName} · ${sheet.rows.length} filas` : 'Da click o arrastra aquí el Excel de marketing'}
					onFile={onFile}
				/>
				<Message type='error' list={prompts} style={{ marginTop: 12 }} />
				{!!result && <div style={{ marginTop: 12 }}>
					<Message type={result.inserted + result.updated > 0 ? 'success' : 'warning'} header={result.inserted + result.updated > 0 ? 'Carga terminada' : 'No se cargó ninguna fila'} list={[
						`${result.inserted} eventos nuevos, ${result.updated} actualizados.`,
						`${result.companies_created} boleteras nuevas.`,
						`${result.promoters_linked} filas ligadas a un promotor, ${result.sellers_linked} a un vendedor.`,
					]} />
					{result.promoters_unmatched.length > 0 && (
						<Message type='warning' header='Promotores que no existen en el CRM' list={result.promoters_unmatched} style={{ marginTop: 8 }} />
					)}
					{result.sellers_unmatched.length > 0 && (
						<Message type='warning' header='Vendedores que no existen en el CRM' list={result.sellers_unmatched} style={{ marginTop: 8 }} />
					)}
					{result.errors.length > 0 && (
						<Message type='error' header='Filas que no se cargaron' list={result.errors.map(e=>`Fila ${e.row}: ${e.reason}`)} style={{ marginTop: 8 }} />
					)}
				</div>}
			</Modal.Content>
			<Modal.Actions>
				<Button text={result ? 'Cerrar' : 'Cancelar'} basic size='small' onClick={bindClick(setUploadOpen, false)} />
				<Button text='Cargar' color='black' size='small' onClick={submit} disabled={!sheet} />
			</Modal.Actions>
		</Modal>
	</div>;
}

export default ExternalEvents;
